/*  
  ✅ Features Identified:
    (4) Step Form with progress tracking

    File upload with preview (max 5 images, 5MB each)

    Price calculation with multiple factors

    WhatsApp integration for order submission

    Local storage for draft saving

    Cloud link (Dropbox/Google Drive) support
 
*/

import React, { useState, useEffect, useMemo } from "react";
import "./OrderForm.css";
import { useOrder } from "../context/OrderContext";
import { useLanguage } from "../context/LanguageContext";
// import { doc, getDoc } from "firebase/firestore";
import { doc, onSnapshot } from "firebase/firestore";
import { db, analytics } from "../firebase";
import { logEvent } from "firebase/analytics";
import { trackEvent } from "../utils/analytics";

// Add this near the top of the file
const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://us-central1-shadi-arts.cloudfunctions.net/api" // Production
    : "http://127.0.0.1:5001/shadi-arts/us-central1/api"; // Local emulator

const urlToBase64 = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const OrderForm = () => {
  const { t } = useLanguage();
  const { selectedReference, clearReference } = useOrder();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Phase 1 / Portrait type :
    portraitType: "",
    subjectCount: "1",
    paperSize: "A4",

    // Phase 2 / addition details :
    backgroundStyle: "simple",
    includeFrame: false,
    urgencyLevel: "normal",

    // Phase 3 :  Personal info
    fullName: "",
    email: "",
    phone: "",
    country: "Egypt",

    // Phase 4 :  Referencial Images :
    referencePhotos: [],
    dropboxLink: "",
    specialInstructions: "",

    // Price property
    estimatedPrice: 0,
    deliveryType: "digital",
    subscriberDiscount: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});

  // Add state for dynamic pricing
  const [dynamicPricing, setDynamicPricing] = useState(null);
  const [pricingLoaded, setPricingLoaded] = useState(false);

  // ✅ حط هنا أول useEffect (Save to localStorage)
  useEffect(() => {
    if (dynamicPricing) {
      localStorage.setItem("shadiArtsPricing", JSON.stringify(dynamicPricing));
      console.log("💾 Pricing saved to localStorage:", dynamicPricing);
    }
  }, [dynamicPricing]);

  // ✅ حط هنا تاني useEffect (Load from localStorage)
  useEffect(() => {
    const savedPricing = localStorage.getItem("shadiArtsPricing");
    if (savedPricing) {
      try {
        const parsed = JSON.parse(savedPricing);
        console.log("📀 Loaded pricing from localStorage:", parsed);
        setDynamicPricing(parsed);
        setPricingLoaded(true);
      } catch (e) {
        console.warn("Failed to parse saved pricing", e);
      }
    }
  }, []);

  // ✅ بعد كده حط الـ useEffect بتاع onSnapshot (Firestore listener)
  useEffect(() => {
    const docRef = doc(db, "pricing", "current");
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const pricingData = docSnap.data();
          console.log("🔥 Live pricing update:", pricingData);
          setDynamicPricing(pricingData);
          setPricingLoaded(true);
        } else {
          console.warn("⚠️ Pricing document not found");
          setPricingLoaded(true);
        }
      },
      (error) => {
        console.error("❌ Firestore pricing listener error:", error);
        setPricingLoaded(true);
      },
    );
    return () => unsubscribe();
  }, []);

  // payment state   :
  const [paymentMethod, setPaymentMethod] = useState("");

  // Helper function to get price directly from dynamicPricing
  const getBasePrice = (portraitType) => {
    if (!dynamicPricing) {
      // Fallback values while loading
      switch (portraitType) {
        case "single":
          return 800;
        case "couple":
          return 1500;
        case "family":
          return 2000;
        case "pet":
          return 700;
        case "celebrity":
          return 1200;
        default:
          return 800;
      }
    }
    return dynamicPricing[portraitType] || 800;
  };

  const getSafePrice = (key, fallback) => {
    if (!dynamicPricing) return fallback;
    const value = dynamicPricing[key];
    return typeof value === "number" ? value : fallback;
  };

  //  Available Portraits types + price policies (for display only)
  const portraitTypes = useMemo(
    () => [
      {
        id: "single",
        name: "Single Portrait",
        basePrice: dynamicPricing?.single || 800,
        description: "One person portrait",
      },
      {
        id: "couple",
        name: "Couple Portrait",
        basePrice: dynamicPricing?.couple || 1500,
        description: "Two people together",
      },
      {
        id: "family",
        name: "Family Portrait",
        basePrice: dynamicPricing?.family || 2000,
        description: "3-5 people",
      },
      {
        id: "pet",
        name: "Pet Portrait",
        basePrice: dynamicPricing?.pet || 700,
        description: "Your beloved pet",
      },
      {
        id: "celebrity",
        name: "Celebrity Portrait",
        basePrice: dynamicPricing?.celebrity || 1200,
        description: "Famous personality",
      },
    ],
    [dynamicPricing],
  );

  // Paper sizes  :
  const paperSizes = [
    { id: "A4", name: "A4 (21x29.7 cm)", priceMultiplier: 1 },
    { id: "A3", name: "A3 (29.7x42 cm)", priceMultiplier: 1.5 },
    { id: "A2", name: "A2 (42x59.4 cm)", priceMultiplier: 2 },
    { id: "custom", name: "Custom Size", priceMultiplier: 2.5 },
  ];

  // Urgent level  :
  const urgencyLevels = [
    { id: "relaxed", name: "Relaxed (4-6 weeks)", priceMultiplier: 1 },
    { id: "normal", name: "Normal (2-3 weeks)", priceMultiplier: 1.2 },
    { id: "urgent", name: "Urgent (1 week)", priceMultiplier: 1.5 },
    { id: "express", name: "Express (3-4 days)", priceMultiplier: 2 },
  ];

  // ==================== Image file handling functions    ====================
  const handleFileUpload = (files) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const maxFiles = 5;

    const validFiles = [];

    Array.from(files).forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        alert(
          `❌ ${file.name} is not a supported image type (JPEG, PNG, WebP only)`,
        );
        return;
      }

      if (file.size > maxSize) {
        alert(`❌ ${file.name} is too large (max 10MB)`);
        return;
      }

      if (formData.referencePhotos.length + validFiles.length >= maxFiles) {
        alert(`❌ Maximum ${maxFiles} photos allowed`);
        return;
      }

      // Image Preview Creation :
      const reader = new FileReader();
      reader.onload = (e) => {
        const photoWithPreview = {
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: e.target.result,
        };

        setFormData((prev) => ({
          ...prev,
          referencePhotos: [...prev.referencePhotos, photoWithPreview],
        }));
      };
      reader.readAsDataURL(file);

      validFiles.push(file);
    });
  };

  const handleFileDrop = (files) => {
    handleFileUpload(files);
  };

  const removePhoto = (index) => {
    setFormData((prev) => ({
      ...prev,
      referencePhotos: prev.referencePhotos.filter((_, i) => i !== index),
    }));
  };

  // ==================== Form Validation ====================
  const validateStep3 = () => {
    const errors = {};

    if (!formData.fullName || formData.fullName.trim().length < 3) {
      errors.fullName = "الاسم الكامل مطلوب (3 أحرف على الأقل)";
    }

    if (
      !formData.email ||
      !formData.email.includes("@") ||
      !formData.email.includes(".")
    ) {
      errors.email = "البريد الإلكتروني غير صالح";
    }

    if (formData.phone && formData.phone.length < 10) {
      errors.phone = "رقم الهاتف غير صالح (10 أرقام على الأقل)";
    }

    if (
      formData.referencePhotos.length === 0 &&
      !formData.dropboxLink &&
      !selectedReference
    ) {
      errors.referencePhotos =
        "يرجى رفع صورة مرجعية واحدة على الأقل أو إضافة رابط سحابي";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ====================  Price Calculation   ====================
  const calculatePrice = () => {
    if (!formData.portraitType) return 0;

    const basePrice = getBasePrice(formData.portraitType);

    const selectedSize = paperSizes.find((s) => s.id === formData.paperSize);
    const selectedUrgency = urgencyLevels.find(
      (u) => u.id === formData.urgencyLevel,
    );

    if (!selectedSize || !selectedUrgency) return 0;

    let price =
      basePrice *
      selectedSize.priceMultiplier *
      selectedUrgency.priceMultiplier;

    if (formData.includeFrame) price += getSafePrice("frame", 300);
    if (formData.deliveryType === "physical")
      price += getSafePrice("delivery_physical", 300);
    if (formData.subjectCount > 1) price *= 0.9;
    if (formData.subscriberDiscount) price *= 0.9;

    return Math.round(price);
  };

  // ====================  [Inputs processing ] ====================
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ====================  transporting betweeen steps  ====================
  const nextStep = () => {
    if (step < 4) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  // ====================  Prepairing  Watsapp messages  ====================
  const createWhatsAppMessage = () => {
    const selectedType = portraitTypes.find(
      (t) => t.id === formData.portraitType,
    );
    const selectedSize = paperSizes.find((s) => s.id === formData.paperSize);
    const selectedUrgency = urgencyLevels.find(
      (u) => u.id === formData.urgencyLevel,
    );

    // ✅ جلب الأسعار الديناميكية
    const framePrice = getSafePrice("frame", 300);
    const deliveryPrice = getSafePrice("delivery_physical", 300);

    // let message =
    //   `🎨 *New Portrait Order - Shadi Arts* 🎨%0A%0A` +
    //   `*Customer Information:*%0A` +
    //   `👤 Name: ${formData.fullName || "Not provided"}%0A` +
    //   `📧 Email: ${formData.email || "Not provided"}%0A` +
    //   `📞 Phone: ${formData.phone || "Not provided"}%0A` +
    //   `📍 Country: ${formData.country}%0A%0A` +
    //   `*Order Details:*%0A` +
    //   `🖼️ Type: ${selectedType?.name || "Not selected"}%0A` +
    //   `📏 Size: ${selectedSize?.name || "Not selected"}%0A` +
    //   `👥 Subjects: ${formData.subjectCount} person(s)%0A` +
    //   `⏱️ Timeline: ${selectedUrgency?.name || "Not selected"}%0A` +
    //   `🖼️ Framing: ${
    //     formData.includeFrame ? `Yes (+EGP ${framePrice})` : "No"
    //   }%0A` +
    //   `🚚 Delivery: ${
    //     formData.deliveryType === "physical"
    //       ? `Physical (+EGP ${deliveryPrice})`
    //       : "Digital (Included)"
    //   }%0A` +
    //   `💰 Estimated Total: *EGP ${calculatePrice()}*%0A%0A` +
    //   `*Special Instructions:*%0A${
    //     formData.specialInstructions || "None provided"
    //   }%0A%0A`;
    let message =
      `🎨 *طلب بورتريه جديد - شادي آرتس* 🎨%0A%0A` +
      `*بيانات العميل:*%0A` +
      `👤 الاسم: ${formData.fullName || "غير مدخل"}%0A` +
      `📧 البريد الإلكتروني: ${formData.email || "غير مدخل"}%0A` +
      `📞 رقم الهاتف: ${formData.phone || "غير مدخل"}%0A` +
      `📍 الدولة: ${formData.country}%0A%0A` +
      `*تفاصيل الطلب:*%0A` +
      `🖼️ نوع البورتريه: ${selectedType?.name || "غير محدد"}%0A` +
      `📏 المقاس: ${selectedSize?.name || "غير محدد"}%0A` +
      `👥 عدد الأشخاص: ${formData.subjectCount} شخص(أشخاص)%0A` +
      `🖼️ إطار: ${formData.includeFrame ? "نعم (+300 جنيه)" : "لا"}%0A` +
      `🚚 التوصيل: ${
        formData.deliveryType === "physical"
          ? "توصيل مادي (+300 جنيه)"
          : "تسليم رقمي (شامل)"
      }%0A` +
      `💰 السعر الإجمالي: *EGP ${calculatePrice()}*%0A%0A` +
      `*تعليمات الدفع:*%0A` +
      `💸 يرجى تحويل المبلغ على أحد المحافظ التالية:%0A` +
      `📱 اتصالات كاش: 01151638804%0A` +
      `📱 فودافون كاش: 01037870724%0A` +
      `🏦 InstaPay: shadisayed@instapay%0A%0A` +
      `📸 *هام جداً:* بعد التحويل، يرجى إرسال صورة واضحة للإيصال (screenshot) على هذا الواتساب.%0A` +
      `⚠️ لن يتم بدء رسم البورتريه إلا بعد استلام صورة الإيصال وتأكيد الدفع.%0A%0A` +
      `*ملاحظات إضافية:*%0A${formData.specialInstructions || "لا توجد"}%0A%0A` +
      `✅ شكراً لثقتك! سيتم إشعارك عند تأكيد الدفع وبدء العمل على بورتريهك.`;

    // Add Payment Instructions based on selected method including method :
    let paymentInstructions = "";
    if (paymentMethod === "EtisalatCash") {
      paymentInstructions =
        `%0A%0A💸 *تعليمات الدفع (اتصالات كاش):*%0A` +
        `📞 رقم المحفظة: 01151638804%0A` +
        `💰 المبلغ: EGP ${calculatePrice()}%0A` +
        `📸 *مطلوب: صورة الإيصال* - بعد التحويل، قم بإرسال صورة الإيصال (screenshot) على هذا الواتساب.%0A` +
        `⚠️ لن يتم بدء رسم البورتريه إلا بعد استلام صورة الإيصال وتأكيد الدفع.%0A` +
        `✅ شكراً لثقتك! سيتم إشعارك عند تأكيد الدفع.`;
    } else if (paymentMethod === "VodafoneCash") {
      paymentInstructions =
        `%0A%0A💸 *تعليمات الدفع (فودافون كاش):*%0A` +
        `📞 رقم المحفظة: 01151638804%0A` +
        `💰 المبلغ: EGP ${calculatePrice()}%0A` +
        `📸 *مطلوب: صورة الإيصال* - بعد التحويل، قم بإرسال صورة الإيصال (screenshot) على هذا الواتساب.%0A` +
        `⚠️ لن يتم بدء رسم البورتريه إلا بعد استلام صورة الإيصال وتأكيد الدفع.%0A` +
        `✅ شكراً لثقتك! سيتم إشعارك عند تأكيد الدفع.`;
    } else if (paymentMethod === "InstaPay") {
      paymentInstructions =
        `%0A%0A💸 *تعليمات الدفع (InstaPay):*%0A` +
        `📧 حساب InstaPay: shadisayed@instapay%0A` +
        `💰 المبلغ: EGP ${calculatePrice()}%0A` +
        `📸 *مطلوب: صورة الإيصال* - بعد التحويل، قم بإرسال صورة الإيصال (screenshot) على هذا الواتساب.%0A` +
        `⚠️ لن يتم بدء رسم البورتريه إلا بعد استلام صورة الإيصال وتأكيد الدفع.%0A` +
        `✅ شكراً لثقتك! سيتم إشعارك عند تأكيد الدفع.`;
    }

    // Add payment instructions to message
    message += paymentInstructions;

    // Add reference portrait info if exists
    if (selectedReference) {
      message +=
        `*🎨 Reference Portrait:*%0A` +
        `🖼️ Title: ${selectedReference.title}%0A` +
        `📂 Category: ${selectedReference.category}%0A` +
        `📝 Description: ${selectedReference.description || "N/A"}%0A` +
        `📏 Size: ${selectedReference.size || "Standard"}%0A` +
        `🔗 Reference Image: ${selectedReference.imageUrl}%0A%0A`;
    }

    // Adding images info or the Watsapp message  :
    if (formData.referencePhotos.length > 0) {
      message += `📸 *Photos Uploaded:* ${formData.referencePhotos.length} photo(s) via website%0A`;
    }

    if (formData.dropboxLink) {
      message += `☁️ *Cloud Link:* ${formData.dropboxLink}%0A%0A`;
    }

    message +=
      `📸 *NEXT STEPS:*%0A` +
      `1️⃣ ${
        formData.referencePhotos.length > 0
          ? "Photos uploaded via website - ready to start!"
          : "Please send REFERENCE PHOTO(S) for the portrait"
      }%0A` +
      `2️⃣ Higher quality photos = Better results%0A` +
      `3️⃣ I'll send a sketch preview before final work%0A%0A` +
      `_Order submitted via Shadi Arts Portfolio Website_`;

    return message;
  };

  // ==================== Sending via WhatsApp ====================
  const submitViaWhatsApp = async () => {
    if (!pricingLoaded) {
      alert("⏳ جاري تحميل الأسعار المحدثة، حاول بعد ثانية...");
      return;
    }

    // Validate first
    if (!validateStep3()) {
      const errorMessages = Object.values(validationErrors).join("\n");
      alert(`⚠️ يرجى تصحيح الأخطاء التالية:\n\n${errorMessages}`);
      return;
    }

    // 🆕 Track checkout started
    try {
      logEvent(analytics, "begin_checkout", {
        currency: "EGP",
        value: calculatePrice(),
        portrait_type: formData.portraitType,
        payment_method: paymentMethod,
        has_reference_photos: formData.referencePhotos.length > 0,
        has_dropbox_link: !!formData.dropboxLink,
        is_subscriber: formData.subscriberDiscount,
      });
      // 🆕 Local Analytics (لـ Admin Dashboard)
      trackEvent("begin_checkout", {
        portrait_type: formData.portraitType,
        payment_method: paymentMethod,
        value: calculatePrice(),
      });
      
     } catch (err) {
      console.warn("Analytics error:", err);
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      const API_URL =
        process.env.NODE_ENV === "production"
          ? "https://us-central1-shadi-arts.cloudfunctions.net/api"
          : "http://127.0.0.1:5001/shadi-arts/us-central1/api";

      let uploadedPhotos = [];

      // رفع الصور اللي الزائر رفعها
      if (formData.referencePhotos.length > 0) {
        setUploadProgress(30);
        const uploadResponse = await fetch(`${API_URL}/upload-photos`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ photos: formData.referencePhotos }),
        });

        setUploadProgress(70);
        const uploadData = await uploadResponse.json();
        if (uploadData.success) {
          uploadedPhotos = uploadData.photos;
        }
      }

      // ✅ إضافة الصورة المرجعية من المعرض (لو موجودة)
      if (selectedReference && selectedReference.imageUrl) {
        try {
          const base64Image = await urlToBase64(selectedReference.imageUrl);
          uploadedPhotos.push({
            data: base64Image,
            name: selectedReference.title,
            size: 0,
            type: "image/jpeg",
          });
        } catch (err) {
          console.warn("❌ Could not convert reference image to base64", err);
        }
      }

      setUploadProgress(85);

      // const orderData = {
      //   customerName: formData.fullName,
      //   email: formData.email,
      //   phone: formData.phone,
      //   country: formData.country,
      //   portraitType: formData.portraitType,
      //   subjectCount: parseInt(formData.subjectCount) || 1,
      //   paperSize: formData.paperSize,
      //   backgroundStyle: formData.backgroundStyle,
      //   includeFrame: formData.includeFrame,
      //   urgencyLevel: formData.urgencyLevel,
      //   referencePhotos: uploadedPhotos,
      //   dropboxLink: formData.dropboxLink,
      //   specialInstructions: formData.specialInstructions,
      //   estimatedPrice: calculatePrice(),
      //   orderDate: new Date().toISOString(),
      //   referencePortrait: selectedReference || null,
      //   paymentMethod: paymentMethod,
      //   subscriberDiscount: formData.subscriberDiscount,
      //   referencePhotoUrl: selectedReference?.imageUrl || null, // دي تضمن إن الرابط موجود دايماً
      // };

      const orderData = {
        customerName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        portraitType: formData.portraitType,

        // 🔽 التعديل هنا: بنبعت قيم الأسعار المحدثة من الـ Firestore مباشرة للـ API
        basePriceSnapshot: getBasePrice(formData.portraitType),
        framePriceSnapshot: getSafePrice("frame", 300),
        deliveryPriceSnapshot: getSafePrice("delivery_physical", 300),

        subjectCount: parseInt(formData.subjectCount) || 1,
        paperSize: formData.paperSize,
        // نبعت برضه قيمة الـ Multiplier عشان الـ Backend يحسب صح لو محتاج
        sizeMultiplier:
          paperSizes.find((s) => s.id === formData.paperSize)
            ?.priceMultiplier || 1,
        urgencyLevel: formData.urgencyLevel,
        urgencyMultiplier:
          urgencyLevels.find((u) => u.id === formData.urgencyLevel)
            ?.priceMultiplier || 1,

        referencePhotos: uploadedPhotos,
        dropboxLink: formData.dropboxLink,
        specialInstructions: formData.specialInstructions,
        estimatedPrice: calculatePrice(), // السعر الكلي المحدث بالكامل من الـ Front
        orderDate: new Date().toISOString(),
        referencePortrait: selectedReference || null,
        paymentMethod: paymentMethod,
        subscriberDiscount: formData.subscriberDiscount,
        referencePhotoUrl: selectedReference?.imageUrl || null,
      };

      const response = await fetch(`${API_URL}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderData),
      });

      const data = await response.json();
      setUploadProgress(100);

      if (data.success) {
        // 🆕 Track successful purchase
        try {
          logEvent(analytics, "purchase", {
            transaction_id: data.orderId,
            value: calculatePrice(),
            currency: "EGP",
            portrait_type: formData.portraitType,
            payment_method: paymentMethod,
            subject_count: parseInt(formData.subjectCount),
            has_reference_photos: uploadedPhotos.length > 0,
            is_subscriber: formData.subscriberDiscount,
          });
          trackEvent("purchase", {
          order_id: data.orderId,
          value: calculatePrice(),
          portrait_type: formData.portraitType,
        });

        } catch (err) {
          console.warn("Analytics error:", err);
        }

        localStorage.setItem(
          "shadiArtsLastOrder",
          JSON.stringify({
            ...orderData,
            orderId: data.orderId,
            date: new Date().toISOString(),
          }),
        );

        // فتح واتساب
        setTimeout(() => {
          if (data.whatsapp?.customer?.url) {
            window.location.href = data.whatsapp.customer.url;
          }
        }, 500);

        setTimeout(() => {
          if (data.whatsapp?.artist?.url) {
            window.open(data.whatsapp.artist.url, "_blank");
          }
        }, 1000);

        alert(
          `✅ تم حفظ الطلب بنجاح!\n\n` +
            `رقم الطلب: ${data.orderId}\n` +
            `السعر الإجمالي: EGP ${calculatePrice()}\n\n` +
            `📸 سيتم فتح واتساب تلقائياً لإتمام الطلب`,
        );

        // عرض خيار مسح النموذج
        if (window.confirm("هل تريد مسح النموذج وبدء طلب جديد؟")) {
          setFormData({
            portraitType: "",
            subjectCount: "1",
            paperSize: "A4",
            backgroundStyle: "simple",
            includeFrame: false,
            urgencyLevel: "normal",
            fullName: "",
            email: "",
            phone: "",
            country: "Egypt",
            referencePhotos: [],
            dropboxLink: "",
            specialInstructions: "",
            estimatedPrice: 0,
            subscriberDiscount: false,
            deliveryType: "digital",
          });
          setStep(1);
        }
      } else {
        throw new Error(data.message || "فشل حفظ الطلب");
      }
    } catch (error) {
      console.error("❌ Error:", error);

      // 🆕 Track failed order
      try {
        logEvent(analytics, "checkout_failed", {
          error_message: error.message,
          portrait_type: formData.portraitType,
          payment_method: paymentMethod,
        });
              trackEvent("checkout_failed", {
        portrait_type: formData.portraitType,
        payment_method: paymentMethod,
        error: error.message,
      });

      } catch (err) {
        console.warn("Analytics error:", err);
      }

      alert(
        `⚠️ حدث خطأ: ${error.message}\n\n` +
          `سيتم فتح واتساب يدوياً مع تفاصيل طلبك.`,
      );
      const fallbackMessage = createWhatsAppMessage();
      window.open(
        `https://wa.me/201151638804?text=${fallbackMessage}`,
        "_blank",
      );
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <section className="order-form-section" id="order">
      <div className="form-container">
        {/* Main  Form Order  Title  */}
        <div className="form-header">
          <h2 className="form-title">
            <span className="title-accent"> {t("Order", "اطلب")} </span>{" "}
            {t("Your Custom Portrait", "بورتريهك المخصص")}
          </h2>
          <p className="form-subtitle">
            {t(
              "Commission a hand-drawn pencil portrait in 4 simple steps",
              "احصل على رسم بورتريه يدوي بالقلم الرصاص في 4 خطوات بسيطة",
            )}
          </p>
        </div>

        {/* Offers + Discounts */}
        <div className="special-offers">
          <div className="offer-card discount-offer">
            <div className="offer-badge">
              🎉 {t("SPECIAL OFFER", "عرض خاص")}{" "}
            </div>
            <div className="offer-content">
              <h4 className="offer-title">
                {t(
                  "Family & Multiple Portraits Discount ",
                  "خصم على البورتريهات العائلية",
                )}
              </h4>
              <p className="offer-description">
                {t(
                  "Order 2+ portraits and get",
                  "اطلب اكتر من 2 بورتريه واحصل",
                )}{" "}
                <strong> {t("10% discount", "خصم 10%")} </strong>{" "}
                {t("on total order!", "على اجمالي الطلبية")}
              </p>
              <div className="offer-details">
                <span className="offer-detail">
                  👥 2+ {t("portraits: 10% off", "خصم 10% ")}{" "}
                </span>
                <span className="offer-detail">
                  👨‍👩‍👧‍👦 3+ {t("portraits: 15% off", "خصم 15% ")}{" "}
                </span>
                <span className="offer-detail">
                  🔄{" "}
                  {t(
                    "Repeat customers: 20% off",
                    "خصم 20 % على الطلبات للعملاء السابقين",
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="offer-card whatsapp-offer">
            <div className="offer-content">
              <h4 className="offer-title">
                {" "}
                {t("Direct WhatsApp Order", "الطلب المباشر عبر الواتساب")}{" "}
              </h4>
              <p className="offer-description">
                {t("Order via WhatsApp for ", "اطلب عبر الواتساب ل ")}{" "}
                <strong> {t("priority processing ", "للأولوية و")} </strong>{" "}
                {t(
                  "and direct communication with the artist!",
                  "التواصل بشكل مباشر مع الفنان!",
                )}
              </p>
              <div className="offer-features">
                <span className="offer-feature">
                  ⚡ {t("Instant response", "الاستجابة المباشرة")}
                </span>
                <span className="offer-feature">
                  💬{" "}
                  {t(
                    "Direct artist communication ",
                    "التواصل بشكل مباشر مع الفنان",
                  )}
                </span>
                <span className="offer-feature">
                  🎨 {t("Customization discussions ", "نقاشات متخصصة ")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="progress-steps">
          {[1, 2, 3, 4].map((stepNum) => (
            <div key={stepNum} className="step-container">
              <div className={`step-circle ${step >= stepNum ? "active" : ""}`}>
                {stepNum}
              </div>
              <div className="step-label">
                {stepNum === 1 && "Portrait Type"}
                {stepNum === 2 && "Details"}
                {stepNum === 3 && "Your Info & Photos"}
                {stepNum === 4 && "Review & Order"}
              </div>
              {stepNum < 4 && <div className="step-connector"></div>}
            </div>
          ))}
        </div>

        {/* MultiSteps Form  */}
        <form className="multi-step-form">
          {/*   step 1 / Protrait Type  */}
          {step === 1 && (
            <div className="form-step animated-step">
              <h3 className="step-title">
                {" "}
                {t("Select Portrait Type ", "اختار نوع البورتريه ")}{" "}
              </h3>

              <div className="portrait-type-grid">
                {portraitTypes.map((type) => (
                  <div
                    key={type.id}
                    className={`type-card ${
                      formData.portraitType === type.id ? "selected" : ""
                    }`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        portraitType: type.id,
                      }))
                    }
                  >
                    <div className="type-icon">
                      {type.id === "single" && "👤"}
                      {type.id === "couple" && "👫"}
                      {type.id === "family" && "👨‍👩‍👧‍👦"}
                      {type.id === "pet" && "🐾"}
                      {type.id === "celebrity" && "⭐"}
                    </div>
                    <h4 className="type-name">{type.name}</h4>
                    <p className="type-description">{type.description}</p>
                    <div className="type-price">
                      {t("Starting from ", "يبدأ من ")} {type.basePrice}{" "}
                      {t("EGP", "ج م")}
                    </div>
                  </div>
                ))}
              </div>

              <div className="size-selection">
                <h4> {t("Select Paper Size", "اختار مقاس الورقة")} </h4>
                <div className="size-options">
                  {paperSizes.map((size) => (
                    <label key={size.id} className="size-option">
                      <input
                        type="radio"
                        name="paperSize"
                        value={size.id}
                        checked={formData.paperSize === size.id}
                        onChange={handleInputChange}
                      />
                      <span className="size-label">{size.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="step-buttons single-button">
                <button
                  type="button"
                  className="btn-next"
                  onClick={nextStep}
                  disabled={!formData.portraitType}
                >
                  {t("Next: Details", "التالي : التفاصيل")} →
                </button>
              </div>
            </div>
          )}

          {/*  Step 2 /  Portrait details    */}
          {step === 2 && (
            <div className="form-step animated-step">
              <h3 className="step-title">
                {" "}
                {t("Additional Details", "تفاصيل اضافية")}{" "}
              </h3>

              <div className="form-group">
                <label> {t("Number of Subjects", "عدد الاشخاص")} </label>
                <select
                  name="subjectCount"
                  value={formData.subjectCount}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  {[1, 2, 3, 4, 5].map((num) => (
                    <option key={num} value={num}>
                      {num} {num === 1 ? "Person" : "People"}
                      {num > 1 ? " (10% discount)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label> {t("Background Style", "نمط الخلفية ")} </label>
                <div className="background-options">
                  {["simple", "detailed", "minimal", "custom"].map((style) => (
                    <label key={style} className="background-option">
                      <input
                        type="radio"
                        name="backgroundStyle"
                        value={style}
                        checked={formData.backgroundStyle === style}
                        onChange={handleInputChange}
                      />
                      <span className="option-label">
                        {style.charAt(0).toUpperCase() + style.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="includeFrame"
                    checked={formData.includeFrame}
                    onChange={handleInputChange}
                  />
                  <span>
                    {" "}
                    {t(
                      "Include Professional Framing (+EGP 100)",
                      "يحتوي على اطار مخصص  (+ 100 ج م)",
                    )}{" "}
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>{t("Delivery Method", "طريقة التوصيل")}</label>
                <div className="delivery-options">
                  <label className="delivery-option">
                    <input
                      type="radio"
                      name="deliveryType"
                      value="digital"
                      checked={formData.deliveryType === "digital"}
                      onChange={handleInputChange}
                    />
                    <span>🖼️ {t("Digital Delivery", "تسليم رقمي ")}</span>
                    <span className="option-price">
                      {t("Included", "مضمن")}
                    </span>
                  </label>
                  <label className="delivery-option">
                    <input
                      type="radio"
                      name="deliveryType"
                      value="physical"
                      checked={formData.deliveryType === "physical"}
                      onChange={handleInputChange}
                    />
                    <span>🚚 {t("Physical Delivery", "توصيل مادي")}</span>
                    <span className="option-price">
                      +{dynamicPricing?.delivery_physical || 100} EGP
                    </span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label> {t("Delivery Timeline", "وقت التوصيل")} </label>
                <div className="urgency-options">
                  {urgencyLevels.map((level) => (
                    <label key={level.id} className="urgency-option">
                      <input
                        type="radio"
                        name="urgencyLevel"
                        value={level.id}
                        checked={formData.urgencyLevel === level.id}
                        onChange={handleInputChange}
                      />
                      <span className="urgency-label">
                        {level.name}
                        <span className="urgency-price">
                          ×{level.priceMultiplier}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="step-buttons">
                <button type="button" className="btn-prev" onClick={prevStep}>
                  ← {t("Back", "الخلف ")}
                </button>
                <button type="button" className="btn-next" onClick={nextStep}>
                  {t(
                    "Next: Your Info & Photos ",
                    "التالي بيانات البورتريه والصور ",
                  )}{" "}
                  →
                </button>
              </div>
            </div>
          )}

          {/* Step 3/ Personal info and Image  */}
          {step === 3 && (
            <div className="form-step animated-step">
              <h3 className="step-title">
                {t(
                  "Your Information & Reference Photos",
                  "بيانات البورتريه ومراجع الصور",
                )}
              </h3>

              <div className="form-group">
                <label> {t("Full Name", "الاسم بالكامل")} *</label>
                <input
                  required
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  style={{
                    borderColor: validationErrors.fullName ? "#ff4444" : "",
                  }}
                />
                {validationErrors.fullName && (
                  <small
                    style={{
                      color: "#ff4444",
                      fontSize: "0.75rem",
                      marginTop: "5px",
                      display: "block",
                    }}
                  >
                    ⚠️ {validationErrors.fullName}
                  </small>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    {" "}
                    {t("Email Address", "عنوان البريد الاكتروني")} *
                  </label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    style={{
                      borderColor: validationErrors.fullName ? "#ff4444" : "",
                    }}
                  />
                  {validationErrors.fullName && (
                    <small
                      style={{
                        color: "#ff4444",
                        fontSize: "0.75rem",
                        marginTop: "5px",
                        display: "block",
                      }}
                    >
                      ⚠️ {validationErrors.fullName}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    {" "}
                    {t("Phone Number (WhatsApp)", "رقم الهاتف(الواتساب)")}{" "}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+20 123 456 7890"
                  />
                </div>
              </div>

              <div className="form-group">
                <label> {t("Country", "الدولة")} </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="Egypt"> {t("Egypt", "مصر")} </option>
                  <option value="Other">
                    {" "}
                    {t("Other Country", "دولة اخرى")}{" "}
                  </option>
                </select>
              </div>

              {/* Selected Reference Portrait Display */}
              {selectedReference && (
                <div
                  className="reference-portrait-card"
                  style={{
                    background: "rgba(212, 175, 55, 0.1)",
                    border: "1px solid var(--gold-primary)",
                    borderRadius: "20px",
                    padding: "20px",
                    marginBottom: "25px",
                    display: "flex",
                    gap: "20px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "12px",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={selectedReference.imageUrl}
                      alt={selectedReference.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        color: "var(--gold-primary)",
                        marginBottom: "5px",
                        fontSize: "1rem",
                      }}
                    >
                      🖼️{" "}
                      {t(
                        "Reference Portrait Selected",
                        "مرجع البورتريه المحدد",
                      )}
                    </h4>
                    <p style={{ margin: 0, fontSize: "0.9rem" }}>
                      <strong>{selectedReference.title}</strong> (
                      {selectedReference.category})
                    </p>
                    <p
                      style={{
                        fontSize: "0.8rem",
                        color: "var(--text-muted)",
                        marginTop: "5px",
                      }}
                    >
                      {t(
                        "This style will be used as reference for your custom portrait",
                        "هذا النمط سيتم استخدامه كمرجع لبورتيهك المخصص",
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={clearReference}
                    style={{
                      background: "transparent",
                      border: "1px solid rgba(212, 175, 55, 0.3)",
                      borderRadius: "50px",
                      padding: "8px 20px",
                      cursor: "pointer",
                      color: "var(--gold-primary)",
                      fontSize: "0.8rem",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(212, 175, 55, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "transparent";
                    }}
                  >
                    ✕ {t("Change Reference", "تغيير المرجع")}
                  </button>
                </div>
              )}

              {/*  Uploading image section  */}
              <div className="photo-upload-section">
                <div className="photo-upload-header">
                  <h4>
                    📸{" "}
                    {t(
                      "Reference Photo(s) - REQUIRED",
                      "الصور المرجعية - مطلوب",
                    )}{" "}
                  </h4>
                  <p className="photo-upload-note">
                    {t(
                      "Upload photos directly or send via WhatsApp after order",
                      "ارفع صورك مباشرة هنا , او عبر الواتساب",
                    )}
                  </p>
                </div>

                {/*  Image direct uploading area  */}
                <div className="upload-method-card">
                  <div className="method-header">
                    <div className="method-icon">⬆️</div>
                    <div className="method-title">
                      <h5>
                        {" "}
                        {t(
                          "Direct Upload (Recommended)",
                          "الرفع المباشر  (موصى به)",
                        )}{" "}
                      </h5>
                      <p className="method-subtitle">
                        {t(
                          "Upload photos directly here",
                          "ارفع صورك مباشرة هنا",
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="method-content">
                    <p>
                      {t(
                        "Upload your reference photos directly. Max 5 photos, 5MB each.",
                        "ارفع صورتك او صورة هنا بحد اقصى 5MB لكل صورة",
                      )}
                    </p>

                    <div
                      className="upload-area"
                      onClick={() =>
                        document.getElementById("photo-upload").click()
                      }
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add("dragover");
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove("dragover");
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove("dragover");
                        handleFileDrop(e.dataTransfer.files);
                      }}
                    >
                      {formData.referencePhotos.length > 0 ? (
                        <div className="upload-preview">
                          <div className="preview-count">
                            📷 {formData.referencePhotos.length}{" "}
                            {t("photo(s)", "صورة")}
                            {t("selected", "تم تحديدها")}
                          </div>
                          <button
                            type="button"
                            className="clear-photos-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFormData((prev) => ({
                                ...prev,
                                referencePhotos: [],
                              }));
                            }}
                          >
                            {t("Clear All", "مسح الكل")}
                          </button>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <div className="placeholder-icon">📤</div>
                          <div className="placeholder-text">
                            <strong>
                              {" "}
                              {t(
                                "Click or drag photos here",
                                "اسحب الصورة هنا",
                              )}{" "}
                            </strong>
                            <p>
                              JPEG, PNG, 5MB{" "}
                              {t("max per photo", "حد اقصى للصورة")}{" "}
                            </p>
                          </div>
                        </div>
                      )}

                      <input
                        id="photo-upload"
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => handleFileUpload(e.target.files)}
                      />
                    </div>

                    {formData.referencePhotos.length > 0 && (
                      <div className="uploaded-photos">
                        <h6> {t("Selected Photos", "اختيار الصورة")} : </h6>
                        <div className="photos-grid">
                          {formData.referencePhotos.map((photo, index) => (
                            <div key={index} className="photo-item">
                              <div className="photo-preview">
                                {photo.preview && (
                                  <img
                                    src={photo.preview}
                                    alt={`Reference ${index + 1}`}
                                  />
                                )}
                              </div>
                              <div className="photo-info">
                                <span className="photo-name">{photo.name}</span>
                                <span className="photo-size">
                                  {(photo.size / 1024 / 1024).toFixed(2)} MB
                                </span>
                              </div>
                              <button
                                type="button"
                                className="remove-photo-btn"
                                onClick={() => removePhoto(index)}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Cloud links  [ for google || Dropbox ]  */}
                <div className="upload-method-card">
                  <div className="method-header">
                    <div className="method-icon">☁️</div>
                    <div className="method-title">
                      <h5>
                        {" "}
                        {t(
                          "Cloud Link (Dropbox/Google Drive)",
                          "لينك تخزين سحابي  (Dropbox/Google Drive)",
                        )}{" "}
                      </h5>
                      <p className="method-subtitle">
                        {" "}
                        {t("Share a download link", "مشاركة لينك التحميل")}{" "}
                      </p>
                    </div>
                  </div>

                  <div className="method-content">
                    <div className="cloud-link-input">
                      <input
                        type="url"
                        placeholder="https://drive.google.com/... or https://dropbox.com/..."
                        value={formData.dropboxLink}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            dropboxLink: e.target.value,
                          }))
                        }
                      />
                      <button
                        type="button"
                        className="paste-link-btn"
                        onClick={async () => {
                          try {
                            const text = await navigator.clipboard.readText();
                            setFormData((prev) => ({
                              ...prev,
                              dropboxLink: text,
                            }));
                          } catch (err) {
                            alert("Cannot access clipboard. Paste manually.");
                          }
                        }}
                      >
                        📋 {t("Paste", "لصق ")}
                      </button>
                    </div>
                    <p className="cloud-note">
                      {t(
                        "Make sure the link is publicly accessible",
                        "تاكد من ان اللينك متاح الوصول",
                      )}
                      <strong> shadiarts.official@gmail.com</strong>
                    </p>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>
                  {" "}
                  {t(
                    "Special Instructions (Optional)",
                    "طلبات خاصة (اختياري)",
                  )}{" "}
                </label>
                <textarea
                  name="specialInstructions"
                  value={formData.specialInstructions}
                  onChange={handleInputChange}
                  placeholder="Any specific details, emotions, or elements you want to include..."
                  rows="4"
                />
              </div>

              <div className="step-buttons">
                <button type="button" className="btn-prev" onClick={prevStep}>
                  ← {t("Back", "الرجوع")}
                </button>
                <button type="button" className="btn-next" onClick={nextStep}>
                  {t("Next: Review & Order", "التالي : المعاينة والطلب")} →
                </button>
              </div>
            </div>
          )}

          {/* Step 4 / Preview + ; Placing  Order    */}
          {step === 4 && (
            <div className="form-step animated-step">
              <h3 className="step-title">
                {t("Review & Place Order", "مراحعة الطلب")}{" "}
              </h3>

              <div className="order-summary">
                <div className="summary-header">
                  <h4> {t("Order Summary", "ملخص الطلب")} </h4>
                  <div className="estimated-price">
                    {t("Total", "الاحمالي")} :{" "}
                    <span className="price-amount">
                      {t("EGP", "ج م")} {calculatePrice()}
                    </span>
                    {formData.subjectCount > 1 && (
                      <span className="discount-badge">
                        🎉{" "}
                        {t(
                          "10% Multi-Subject Discount Applied",
                          "تم تطبيق خصم 10% ",
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {/* Price details  (EG)  */}
                <div className="price-breakdown">
                  <div className="breakdown-item">
                    <span className="breakdown-label">
                      {t("Base Price", "التكلفة الاساسية")} :
                    </span>
                    <span className="breakdown-value">
                      {t("EGP", "ج م")} {getBasePrice(formData.portraitType)}
                    </span>
                  </div>

                  <div className="breakdown-item">
                    <span className="breakdown-label">
                      {t("Size", "الحجم")} ({formData.paperSize}):
                    </span>
                    <span className="breakdown-value">
                      ×
                      {
                        paperSizes.find((s) => s.id === formData.paperSize)
                          ?.priceMultiplier
                      }
                    </span>
                  </div>

                  <div className="breakdown-item">
                    <span className="breakdown-label">
                      {" "}
                      {t("Timeline", "الاطار الزمني")} :
                    </span>
                    <span className="breakdown-value">
                      ×
                      {
                        urgencyLevels.find(
                          (u) => u.id === formData.urgencyLevel,
                        )?.priceMultiplier
                      }
                    </span>
                  </div>

                  {formData.includeFrame && (
                    <div className="breakdown-item">
                      <span className="breakdown-label">
                        {t("Professional Framing:", "اطار مخصص")}
                      </span>
                      <span className="breakdown-value">
                        + {t("EGP", "ج م")} {getSafePrice("frame", 300)}
                      </span>
                    </div>
                  )}

                  {formData.subjectCount > 1 && (
                    <div className="breakdown-item discount">
                      <span className="breakdown-label">
                        {formData.subjectCount}
                        {formData.subjectCount > 1 ? "s" : ""}{" "}
                        {t("Discount:", "الخصم:")}
                      </span>
                      <span className="breakdown-value">-10%</span>
                    </div>
                  )}

                  <div className="breakdown-total">
                    <span className="total-label">
                      {" "}
                      {t("Estimated Total:", "الاحمالي :")}{" "}
                    </span>
                    <span className="total-value">
                      {" "}
                      {t("ُEGP", "ح م")} {calculatePrice()}
                    </span>
                  </div>
                </div>

                {/* Order Options */}
                <div className="order-options">
                  <h5> {t("Complete Your Order", "أكمل طلبك")} </h5>

                  {/* 🆕 ADD YOUTUBE DISCOUNT CHECKBOX HERE */}
                  <div className="discount-section">
                    <label className="discount-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.subscriberDiscount}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            subscriberDiscount: e.target.checked,
                          }));
                        }}
                      />
                      <span>
                        🎬{" "}
                        {t(
                          "I'm subscribed to Shadi Arts on YouTube (10% discount)",
                          "أنا مشتر ك في قناة اليوتيوب Shadi Arts (خصم 10%)",
                        )}
                      </span>
                    </label>
                    <p className="discount-note">
                      *{" "}
                      {t(
                        "By checking this box, you confirm you're a subscriber.Thank you for your honesty and support!",
                        "باختيارك لذا الاختيار انت تؤكد على انك مشترك في قناة اليوتيوب شكرا لدعمك",
                      )}{" "}
                      🙏
                    </p>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="payment-method-section">
                    <h6>
                      💰 {t("Select Payment Method", "اختار طريقة الدفع")}{" "}
                    </h6>
                    <div className="payment-options">
                      <label
                        className={`payment-option ${paymentMethod === "EtisalatCash" ? "selected" : ""}`}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="EtisalatCash"
                          checked={paymentMethod === "EtisalatCash"}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                        <span className="payment-icon">📱</span>
                        <span className="payment-name">
                          {" "}
                          {t("Etisalat Cash", "اتصالات كاش")}{" "}
                        </span>
                        <span className="payment-desc">
                          {" "}
                          {t(
                            "Direct Transfer from Your Etisalat Wallet",
                            "تحويل فوري من محفظتك إتصالات",
                          )}{" "}
                        </span>
                      </label>

                      <label
                        className={`payment-option ${paymentMethod === "VodafoneCash" ? "selected" : ""}`}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="VodafoneCash"
                          checked={paymentMethod === "VodafoneCash"}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                        <span className="payment-icon">📱</span>
                        <span className="payment-name">
                          {t("Vodafone Cash", "فودافون كاش")}
                        </span>
                        <span className="payment-desc">
                          {t(
                            "Direct Transfer from Your Vodafone Wallet",
                            "تحويل فوري من محفظتك فودافون",
                          )}
                        </span>
                      </label>

                      <label
                        className={`payment-option ${paymentMethod === "InstaPay" ? "selected" : ""}`}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="InstaPay"
                          checked={paymentMethod === "InstaPay"}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                        <span className="payment-icon">🏦</span>
                        <span className="payment-name">InstaPay</span>
                        <span className="payment-desc">
                          {t(
                            "Transfer through your Instapay Account",
                            "تحويل من خلال حسابك في الانستاباي",
                          )}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* WhatsApp Order Option */}
                  <div className="order-option primary-option">
                    <div className="option-header">
                      <div className="option-icon">💬</div>
                      <div className="option-title">
                        <h6>
                          {t(
                            "Order via WhatsApp (Recommended)",
                            "اطلب عبر الواتس اب (موصي به)",
                          )}
                        </h6>
                        <p className="option-subtitle">
                          {t("Fast, Direct, Secure", "سرعة , دقة ,أمان")}
                        </p>
                      </div>
                    </div>

                    <p className="option-description">
                      {formData.referencePhotos.length > 0
                        ? `✅ ${formData.referencePhotos.length} photo(s) uploaded - ready to go!`
                        : "Send order details directly to my WhatsApp. Photos can be sent in the chat."}
                    </p>

                    <button
                      type="button"
                      className="btn-whatsapp"
                      onClick={() => {
                        logEvent(analytics, "whatsapp_click", {
                          source: "order_form",
                          has_photos: formData.referencePhotos.length > 0,
                        });
                        submitViaWhatsApp();
                      }}
                      disabled={
                        isSubmitting ||
                        !pricingLoaded ||
                        !formData.fullName ||
                        !formData.email ||
                        !paymentMethod
                      }
                    >
                      {isSubmitting
                        ? uploadProgress > 0
                          ? `⏳ جاري التحميل ${uploadProgress}%...`
                          : "⏳ جاري المعالجة..."
                        : "💬 تأكيد الطلب عبر واتساب"}
                    </button>

                    {!paymentMethod && (
                      <p className="payment-warning">
                        ⚠️
                        {t(
                          "Please choose payment method first",
                          "يرجى اختيار وسيلة الدفع أولاً",
                        )}
                      </p>
                    )}

                    <p className="option-note">
                      {formData.referencePhotos.length > 0
                        ? "Photos already included in order"
                        : "You'll send photos after order confirmation"}
                    </p>
                  </div>
                </div>

                {/*  Policies and Insturctions  */}
                <div className="order-policies">
                  <div className="policy-item">
                    <span className="policy-icon">🛡️</span>
                    <div className="policy-content">
                      <h6>
                        {t(
                          "100% Satisfaction Guarantee",
                          "ضمان مستوى رضا  100% ",
                        )}
                      </h6>
                      <p>
                        {t(
                          " Unlimited revisions until you're completely satisfied.",
                          "مراحعة غير محدودة حالة عدم الكامل ",
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="policy-item">
                    <span className="policy-icon">⚡</span>
                    <div className="policy-content">
                      <h6> {t("Fast Response Time", "وقت سرعة الاستجابة")} </h6>
                      <p>
                        {t(
                          "Response within 2-4 hours on WhatsApp.",
                          "الاستجابة خلال 2-4 ساعات على الواتساب",
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="policy-item">
                    <span className="policy-icon">🎁</span>
                    <div className="policy-content">
                      <h6> {t("Loyalty Discount", "خصم الاشتراك")} </h6>
                      <p>
                        {t(
                          "15% off your next order after completing this one!",
                          "احصل على خصم 15% في حالة اشتراكك في قناة اليوتي",
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="step-buttons">
                <button type="button" className="btn-prev" onClick={prevStep}>
                  ← {t("Modify Order", "تعديل الطلب")}
                </button>
                <button
                  type="button"
                  className="btn-save"
                  onClick={() => {
                    localStorage.setItem(
                      "shadiArtsDraftOrder",
                      JSON.stringify({
                        ...formData,
                        referencePhotos: formData.referencePhotos.map((p) => ({
                          name: p.name,
                          size: p.size,
                        })),
                      }),
                    );
                    alert(
                      "✅ Order saved as draft! You can complete it later.",
                    );
                  }}
                >
                  💾 {t("Save as Draft", "حفظ كمسودة")}
                </button>
              </div>
            </div>
          )}
        </form>

        {/*  Quick Order Options  */}
        <div className="quick-orders">
          <h4 className="quick-orders-title">
            {t("Quick Order Options", "خيارات الطلب السريع")}
          </h4>
          <p className="quick-orders-subtitle">
            {t(
              "Popular portrait types for fast ordering",
              "انواع البورتريهات الشائعة للطلب السريع",
            )}
          </p>

          <div className="quick-order-buttons">
            <button
              className="quick-order-btn"
              onClick={() => {
                setStep(1);
                setFormData((prev) => ({
                  ...prev,
                  portraitType: "single",
                  paperSize: "A4",
                  urgencyLevel: "normal",
                  subjectCount: "1",
                }));
              }}
            >
              <span className="quick-icon">👤</span>
              <span className="quick-text">
                {t("Single Portrait", "البورتيه الفردي")}{" "}
              </span>
              <span className="quick-price">EGP {getBasePrice("single")}</span>
            </button>

            <button
              className="quick-order-btn"
              onClick={() => {
                setStep(1);
                setFormData((prev) => ({
                  ...prev,
                  portraitType: "couple",
                  subjectCount: "2",
                  paperSize: "A3",
                  urgencyLevel: "normal",
                }));
              }}
            >
              <span className="quick-icon">👫</span>
              <span className="quick-text">
                {t("Couple Portrait", "البورتيه المزدوج")}
              </span>
              <span className="quick-price">EGP {getBasePrice("couple")}</span>
            </button>

            <button
              className="quick-order-btn"
              onClick={() => {
                setStep(1);
                setFormData((prev) => ({
                  ...prev,
                  portraitType: "family",
                  subjectCount: "4",
                  paperSize: "A2",
                  urgencyLevel: "relaxed",
                }));
              }}
            >
              <span className="quick-icon">👨‍👩‍👧‍👦</span>
              <span className="quick-text">
                {t("Family Portrait", "البورتيه العائلي")}
              </span>
              <span className="quick-price">EGP {getBasePrice("family")}</span>
            </button>
          </div>

          <div className="whatsapp-direct">
            <button
              className="whatsapp-direct-btn"
              onClick={() =>
                window.open("https://wa.me/201151638804", "_blank")
              }
            >
              <div className="whatsapp-btn-content">
                <div className="whatsapp-icon-wrapper">
                  <span className="whatsapp-icon">💬</span>
                  <span className="whatsapp-badge">24/7</span>
                </div>
                <div className="whatsapp-text-wrapper">
                  <span className="whatsapp-title">
                    {t("Direct WhatsApp Line", "خط التواصل المباشر عبر الواتس")}
                  </span>
                  <span className="whatsapp-text">
                    {t(
                      "Message me for custom requests & urgent orders",
                      "تواصل معي للطلبات المخصصة او الطلبات المستعجلة",
                    )}
                  </span>
                </div>
                <div className="whatsapp-arrow-wrapper">
                  <span className="whatsapp-arrow">→</span>
                  <span className="whatsapp-arrow-hover">⚡</span>
                </div>
              </div>
            </button>

            <div className="whatsapp-features">
              <div className="feature-chip">
                <span className="feature-icon">⚡</span>
                <span>
                  {t("Response within 2-4 hours", "الاستجابة خلال 24  ساعة")}
                </span>
              </div>
              <div className="feature-chip">
                <span className="feature-icon">🎨</span>
                <span>
                  {t("Direct artist communication", "التواصل مباشرة مع الفنان")}
                </span>
              </div>
              <div className="feature-chip">
                <span className="feature-icon">🌍</span>
                <span>
                  {t(
                    "Local Shipping inside Egypt only, Worldwide Digital shipping available",
                    "متاح التوصيل داخل مصر فقط , متاح رقميا خارح مصر ",
                  )}
                </span>
              </div>
            </div>

            <p className="whatsapp-note">
              <span className="note-star">✦</span>
              {t(
                "For complex custom portraits, bulk orders, or special requests",
                "للحصول على صور شخصية معقدة حسب الطلب، أو طلبات بالجملة، أو طلبات خاصة",
              )}
              <span className="note-star">✦</span>
            </p>
          </div>

          {/* <div className="whatsapp-direct">
            <button
              className="whatsapp-direct-btn"
              onClick={() =>
                window.open("https://wa.me/201151638804", "_blank")
              }
            >
              <span className="whatsapp-icon">💬</span>
              <span className="whatsapp-text">
                Message directly on WhatsApp for custom requests
              </span>
              <span className="whatsapp-arrow">→</span>
            </button>
            <p className="whatsapp-note">
              For urgent orders or complex custom requests
            </p>
          </div> */}
        </div>

        {/* Addtional info  */}
        <div className="form-footer">
          <div className="guarantee-card">
            <div className="guarantee-icon">✅</div>
            <h5> {t("Quality Guarantee", "ضمان الجودة")} </h5>
            <p>
              {t(
                "Every portrait is drawn with professional grade materials and techniques.",
                "يتم رسم كل صورة باستخدام مواد وتقنيات احترافية.",
              )}
            </p>
          </div>

          <div className="process-info">
            <h5> {t("Process Timeline", "الجدول الزمني للعملية")} </h5>
            <ul>
              <li>
                📝
                {t(
                  "Order confirmation within 24 hours",
                  "تأكيد الطلب خلال 24 ساعة ",
                )}
              </li>
              <li>
                ✏️
                {t(
                  "Work begins after order confirmation",
                  "البدء في العمل بعد تأكيد الطلب ",
                )}
              </li>
              <li>
                📸
                {t("Progress updates provided", "تقديم تحديثات عن سير العمل")}
              </li>
              <li>
                📦
                {t("Digital preview before shipping", "معاينة رقمية قبل الشحن")}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderForm;
